import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Plus, UserCircle, Trash, X as XIcon, Storefront } from "@phosphor-icons/react";

const TYPES = ["seller", "distributor", "manufacturer", "wholesaler", "dark_web", "broker", "courier", "other"];
const COUNTRIES = ["IN", "BD", "PK", "SG", "AE", "EG", "CH", "UK", "US", "NP", "TH"];

export default function Parties() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [country, setCountry] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    const params = {};
    if (country) params.country = country;
    if (type) params.type = type;
    const { data } = await api.get("/parties", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [country, type]);

  const filtered = items.filter((p) =>
    !q || p.name?.toLowerCase().includes(q.toLowerCase()) ||
    (p.aliases || []).some((a) => a.toLowerCase().includes(q.toLowerCase())) ||
    p.city?.toLowerCase().includes(q.toLowerCase())
  );

  const remove = async (id) => {
    if (!window.confirm("Delete this party?")) return;
    await api.delete(`/parties/${id}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="parties-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Library</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Suspect Parties</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">{items.length} infringers, partners, intermediaries</div>
        </div>
        <button data-testid="add-party-btn" onClick={() => setShowAdd(true)} className="span-btn">
          <Plus size={14} /> Add party
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="hairline px-3 h-10 surface flex items-center w-full md:w-[280px]">
          <input data-testid="party-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, alias, city…" className="bg-transparent w-full text-[13px] outline-none"/>
        </div>
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="span-input w-[140px]">
          <option value="">All countries</option>
          {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className="span-input w-[180px]">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>

      <div className="card-flat">
        <table className="span-table">
          <thead><tr><th>Name</th><th>Type</th><th>City</th><th>Country</th><th>Aliases</th><th>Contact</th><th>Cases</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-[12px] text-[var(--span-text-2)]">No parties yet.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.party_id} data-testid={`party-row-${p.party_id}`}>
                <td className="font-medium flex items-center gap-2"><UserCircle size={15}/> {p.name}</td>
                <td><span className="badge">{p.type}</span></td>
                <td>{p.city || "—"}</td>
                <td className="font-mono text-[11px]">{p.country_code}</td>
                <td className="text-[12px] text-[var(--span-text-2)]">{(p.aliases || []).join(", ") || "—"}</td>
                <td className="text-[12px]">{p.email || p.phone || "—"}</td>
                <td className="tnum">{p.case_count || 0}</td>
                <td className="text-[var(--span-text-2)]">{fmtDate(p.created_at)}</td>
                <td>
                  <button data-testid={`delete-party-${p.party_id}`} onClick={() => remove(p.party_id)} className="text-[var(--span-text-2)] hover:text-[var(--span-danger)]">
                    <Trash size={14}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", type: "seller", country_code: "IN", city: "", aliases: "", phone: "", email: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setBusy(true);
    try {
      await api.post("/parties", {
        ...form,
        aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      });
      onSaved();
    } catch (e) {
      alert("Could not save: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="add-party-modal">
      <form onSubmit={submit} className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow">New party</div>
          <button type="button" onClick={onClose}><XIcon size={16}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-1">Name / Entity *</label>
            <input data-testid="party-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="span-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Type</label>
              <select value={form.type} onChange={(e) => set("type", e.target.value)} className="span-input">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Country</label>
              <select value={form.country_code} onChange={(e) => set("country_code", e.target.value)} className="span-input">
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">City</label>
            <input value={form.city} onChange={(e) => set("city", e.target.value)} className="span-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Aliases (comma-separated)</label>
            <input value={form.aliases} onChange={(e) => set("aliases", e.target.value)} placeholder="KB Traders, Karol Bagh Wholesale" className="span-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Phone</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="span-input"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Email</label>
              <input value={form.email} onChange={(e) => set("email", e.target.value)} className="span-input"/>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="span-input"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button data-testid="submit-party-btn" disabled={busy} className="span-btn"><Plus size={12}/> Save</button>
        </div>
      </form>
    </div>
  );
}
